import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AccessExpiryBanner from '../components/AccessExpiryBanner';
import StatusBadge from '../components/StatusBadge';
import { ErrorBanner, InfoBanner } from '../components/Feedback';
import { api } from '../api/client';
import { absent } from '../absent';
import { useAuth } from '../context/AuthContext';

/**
 * Module 12 — the Teacher dashboard: verification status, quick links to resources, and
 * the code distribution summary.
 *
 * <p>It leads with whatever is blocking them. A teacher who is not verified, or whose
 * school is not linked, or whose school has adopted nothing, sees one sentence saying so
 * rather than a grid of zeroes they have to interpret.
 */
export default function TeacherDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.teacherDashboard().then(setData).catch(setError);
  }, []);

  return (
    <>
      <div className="page-head">
        <h1>Welcome, {user?.fullName?.split(' ')[0]}</h1>
        <p>{data?.schoolName ? data.schoolName : 'Your account and your resources.'}</p>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      {!data && !error && <div className="empty">Loading…</div>}

      {data && (
        <>
          {/* Module 11 — above the next step, because access closing outranks
              whatever else is outstanding. */}
          <AccessExpiryBanner
            status={data.accessStatus}
            daysRemaining={data.accessDaysRemaining}
            what="Your access to the resource repository"
            action="Contact your Fountainhead representative to renew it."
          />

          {/* The annual reminder, in the month the schools renew in. The server
              withholds it while the banner above is showing — the date three weeks
              away says more than the season in general. */}
          {data.renewalNotice && <InfoBanner>{data.renewalNotice}</InfoBanner>}

          {data.nextStep && <InfoBanner>{data.nextStep}</InfoBanner>}

          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-label">Verification</div>
              <div style={{ marginTop: 10 }}>
                <StatusBadge status={data.verificationStatus} />
              </div>
              <div className="kpi-note">
                {/* absent, not === null: a teacher with no window yet has no
                    accessDaysRemaining field at all, and === null let it through to
                    read "undefined days of access left". */}
                {absent(data.accessDaysRemaining)
                  ? 'No access window yet'
                  : `${data.accessDaysRemaining} days of access left`}
              </div>
            </div>
            <Kpi label="Books" value={data.adoptedTitles} note="Adopted by your school" />
            <Kpi label="Resources" value={data.resourcesAvailable}
                 note={`Across ${data.chaptersAvailable} chapters`} />
            <Kpi label="Codes to hand out" value={data.codesToHandOut}
                 note={`${data.codesUsedAtSchool.toLocaleString()} already used`} />
          </div>

          <div className="panel-grid">
            <section className="card card-pad panel">
              <h2>Your resources</h2>
              <p className="panel-sub">
                {data.resourceAccess
                  ? 'Manuals, lesson plans, worksheets and videos for the books your school has adopted.'
                  : 'These unlock once your account is verified.'}
              </p>
              <p className="hint">
                {data.resourceAccess ? (
                  <Link to="/repository">Open the resource repository</Link>
                ) : (
                  <Link to="/profile">Check your account</Link>
                )}
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>Student codes</h2>
              <p className="panel-sub">
                {data.codesToHandOut > 0
                  ? `${data.codesToHandOut.toLocaleString()} codes are waiting to be given to students.`
                  : 'Your school has no codes left to hand out.'}
              </p>
              <p className="hint">
                <Link to="/codes">Open student codes</Link>
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>Your school</h2>
              <p className="panel-sub">
                {data.schoolLinked
                  ? `${data.schoolName} — linked in the Fountainhead directory.`
                  : 'Not linked to a school in the directory yet.'}
              </p>
              {!data.schoolLinked && (
                <p className="hint">
                  Activation codes are allocated to a school, so an unlinked account
                  cannot receive any. Your Fountainhead contact can link it.
                </p>
              )}
            </section>
          </div>

          <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>
            Counted at {new Date(data.generatedAt).toLocaleTimeString()}.
          </p>
        </>
      )}
    </>
  );
}

function Kpi({ label, value, note }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value.toLocaleString()}</div>
      {note && <div className="kpi-note">{note}</div>}
    </div>
  );
}
